import _ from "lodash";
import { bindActionCreators } from "redux";
import { connect, useSelector } from "react-redux";
import moment from "moment-timezone";
import React, { useEffect, useRef, useState } from "react";
import { withRouter } from "react-router-dom";

import { api } from "@/APIRequests";
import { disableLoginPopUps, fetchActivityList, updateScheduleList } from "@/redux/actions/patient";
import { getUsernameFromToken, updateReduxProfile } from "@/redux/actions/auth";
import HCPAutoCancelDialog from "@/containers/Dashboard/Patient/HCPAutoCancelDialog";
import { PatientPaymentBanner } from "@/component-library";
import { redirectOnSignIn } from "@/utils/redirectOnSignIn";
import { showAlertWithActionAndCustomButton } from "@/redux/actions/alerts";

import InstantCall from "../InstantCall";
import { Portal } from "../../Common/Modal/Portal";

import {
  AccomplishmentsWidget,
  buildPatientAccomplishmentMilestones,
  buildPatientAccomplishmentSessions,
} from "./Widget/AccomplishmentsWidget";
import CareTeamWidget from "./Widget/CareTeamWidget";
import { DIReminderWidget } from "./Widget/DIReminderWidget";
import FamilyMembersWidget from "./Widget/FamilyMembersWidget";
import { InactiveModal } from "./InactiveModal";
import MessagesWidget from "./Widget/MessagesWidget";
import NextSessionWidget from "./Widget/NextSessionWidget";
import NextStepWidget from "./Widget/NextStepWidget";
import ScheduleWidget from "./Widget/ScheduleWidget";
import { TasksWidget } from "./Widget/TasksWidget";
import { WaitListModal } from "./WaitListModal";
import "./styles.scss";

const MemberHome = (props) => {
  const {
    allowInactivePopUp,
    allowWaitListPopUp,
    daysUntilPasswordReset,
    dispatch,
    profile,
    scheduleList,
    totalUnreadMessages,
  } = props;
  const showPaywall = useSelector((state) => state.patientCardInfo?.showPaywall);

  // Track component mount status to prevent setState on unmounted component
  const isMountedRef = useRef(false);

  const [sendBirdContacts, setSendBirdContacts] = useState(null);
  const [sendBirdClient, setSendBirdClient] = useState(null);
  const [accomplishments, setAccomplishments] = useState(null);
  const [showAutoCancelledSession, setShowAutoCancelledSession] = useState(false);
  const [autoCancelledSession, setAutoCancelledSession] = useState(null);

  const processSendBirdChannel = (user, channel) => {
    channel.otherMembers = channel.members.filter((member) => member.userId !== user.user_id);

    channel.otherMembersMap = channel.otherMembers.reduce((map, member) => {
      map[member.userId] = member;
      map[member.userId]["unreadMessageCount"] = channel.unreadMessageCount;
      map[member.userId]["lastMessage"] = channel.lastMessage;

      return map;
    }, {});

    return channel;
  };

  const processSendBirdMembers = (map, channel) => {
    Object.entries(channel.otherMembersMap).map(([userId, member]) => {
      map[userId] = member;
      map[userId]["unreadMessageCount"] = member.unreadMessageCount;
      map[userId]["lastMessage"] = channel.lastMessage;
      map[userId]["group"] = member.metaData.group;
      // If a member is assigned a prescriber, but the prescriber has not messaged the member previously,
      // we should not display the prescriber name here
      map[userId]["shouldShow"] =
        channel.lastMessage || member.metaData.group !== "provider:prescribe";
    });

    return map;
  };

  const getActivities = async (type) => {
    const options = { params: { showLoader: false } };
    const urlParams = { type };

    return await fetchActivityList({ dispatch, options, urlParams });
  };

  function fetchSendBirdData() {
    try {
      (async () => {
        const options = { params: { showLoader: false } };
        let user = await api.messaging.fetch_user({ options });
        let client = api.messaging.create_client();

        if (!isMountedRef.current) return;
        setSendBirdClient(client);
        await api.messaging.connect_to_sendbird(client, user.user_id, user.access_token);
        const { channels } = await api.messaging.get_paginated_channels(client);
        const contacts = channels
          .map((channel) => processSendBirdChannel(user, channel))
          .reduce(processSendBirdMembers, {});

        if (!isMountedRef.current) return;
        if (contacts && Object.keys(contacts).length > 0) {
          setSendBirdContacts(contacts);
        } else {
          setSendBirdContacts({});
        }
      })();
    } catch (e) {
      console.log(e);
    }
  }

  useEffect(() => {
    redirectOnSignIn(props.history);
    props.updateScheduleList();
  }, []);

  useEffect(() => {
    isMountedRef.current = true;

    if (profile && profile.care_team_members) {
      fetchSendBirdData();
    }
    if (profile) {
      const fetchActivities = async () => {
        const completedSessions = await getActivities("schedule");

        if (!isMountedRef.current) return;
        setAccomplishments({
          memberMilestoneActivities: buildPatientAccomplishmentMilestones(profile),
          memberSessionActivities: buildPatientAccomplishmentSessions(completedSessions),
        });

        if (
          profile.group_type === "hcp_program" &&
          !("hcp_auto_cancellation" in profile.acknowledgements)
        ) {
          const cancelledSessions = await getActivities("cancelled");
          const autoCancelledSessionArray = cancelledSessions.filter(
            (session) => session.key_content.is_auto_cancelled,
          );
          const autoCancelledSession =
            autoCancelledSessionArray.length && autoCancelledSessionArray[0];

          if (autoCancelledSession && isMountedRef.current) {
            setShowAutoCancelledSession(true);
            setAutoCancelledSession(autoCancelledSession);
          }
        }
      };

      fetchActivities();
    }

    return () => {
      isMountedRef.current = false;
      if (sendBirdClient) {
        sendBirdClient.removeAllChannelHandlers();
        sendBirdClient.disconnect();
      }
    };
  }, [profile, totalUnreadMessages]);

  useEffect(() => {
    if (!(daysUntilPasswordReset == null)) {
      if (daysUntilPasswordReset <= 7) {
        props.showAlertWithActionAndCustomButton(
          `For security best practices and your protection aptihealth requires you to change your password every 90 days. Your password is set to expire in ${daysUntilPasswordReset} days.`,
          () => {
            props.history.push("/app/change-password");
          },
          "Change Password",
        );
      }
    }
  }, [daysUntilPasswordReset]);

  const acknowledgedHCPDI = async () => {
    try {
      const acknowledgement = {
        acknowledged_by: getUsernameFromToken(),
        client: "web",
        date: moment.utc().toISOString(),
        timezone: moment.tz.guess(),
      };

      const data = {
        acknowledgement: acknowledgement,
        acknowledgement_name: "hcp_di_reminder",
      };

      const options = { params: { showLoader: false } };

      // TODO: replace in 5457
      // const updatedProfile = await api.patient.append_acknowledgement({ data, options });
      await api.patient.append_acknowledgement({ data, options });

      // profile is fetched here to correctly grab the tasks from the task service,
      // this will need to be replaced with a call to the task service in https://aptihealth.atlassian.net/browse/AT1-4676
      // created https://aptihealth.atlassian.net/browse/AT1-5457 to account for this
      const result = await api.auth.fetch_profile({ options: { showLoader: true } });

      // TODO: replace in 5457
      // props.updateReduxProfile(updatedProfile);
      props.updateReduxProfile(result.user);
    } catch (e) {
      console.log(e);
    }
  };

  const acknowledgeHCPAutoCancellation = async () => {
    try {
      const acknowledgement = {
        acknowledged_by: getUsernameFromToken(),
        activity_id: autoCancelledSession.entity_id,
        client: "web",
        date: moment.utc().toISOString(),
        timezone: moment.tz.guess(),
      };

      const data = {
        acknowledgement: acknowledgement,
        acknowledgement_name: "hcp_auto_cancellation",
      };

      const options = { params: { showLoader: false } };

      // TODO: replace in 5464
      // const updatedProfile = await api.patient.append_acknowledgement({ data, options });
      await api.patient.append_acknowledgement({ data, options });

      // profile is fetched here to correctly grab the tasks from the task service,
      // this will need to be replaced with a call to the task service in https://aptihealth.atlassian.net/browse/AT1-4676
      // created https://aptihealth.atlassian.net/browse/AT1-5464 to account for this
      const result = await api.auth.fetch_profile({ options: { showLoader: true } });

      // TODO: replace in 5464
      // props.updateReduxProfile(updatedProfile);
      props.updateReduxProfile(result.user);
    } catch (e) {
      console.log(e);
    }
  };

  const shouldRenderInactiveLoginModal =
    profile && shouldShowInactiveModal(profile) && allowInactivePopUp;

  const shouldRenderWaitListLoginModal =
    profile &&
    !shouldRenderInactiveLoginModal &&
    shouldShowWaitListModal(profile) &&
    allowWaitListPopUp;

  const firstName = profile.preferred_name || profile.first_name;

  return (
    <div data-public="true">
      {shouldRenderInactiveLoginModal && (
        <Portal
          className="take-assessment-modal"
          headerRender={<h5 className="border-bottom-0 tw:font-bold"></h5>}
          isBottomCloseActive={false}
          isOpenOnRender={true}
          onModalClose={() => props.disableLoginPopUps()}
        >
          <InactiveModal />
        </Portal>
      )}

      {shouldRenderWaitListLoginModal && (
        <Portal
          className="take-assessment-modal"
          headerRender={<h5 className="border-bottom-0 tw:font-bold"></h5>}
          isBottomCloseActive={false}
          isOpenOnRender={true}
          onModalClose={() => props.disableLoginPopUps()}
        >
          <WaitListModal />
        </Portal>
      )}

      {profile && scheduleList && scheduleList.length && shouldShowHCPDIModal(profile) ? (
        <Portal
          className="take-assessment-modal"
          headerClass="portal-grey-background"
          headerRender={
            <div className="d-flex flex-column align-items-center w-100 mt-2 tw:justify-center">
              <h5
                className="border-bottom-0 text-align-center fs-lg-20 fs-14 tw:font-bold"
                style={{ color: "#0E3C51" }}
              >
                Welcome to aptihealth!
              </h5>
              <h5
                className="border-bottom-0 text-align-center fs-lg-20 fs-14 tw:font-bold"
                style={{ color: "#0E3C51" }}
              >
                Your first session is coming up.
              </h5>
            </div>
          }
          isBottomCloseActive={false}
          isOpenOnRender={true}
          onModalClose={acknowledgedHCPDI}
        >
          <DIReminderWidget call={scheduleList[0]} onSubmit={acknowledgedHCPDI} />
        </Portal>
      ) : null}

      {profile && !isInactive(profile) && showAutoCancelledSession && autoCancelledSession && (
        <Portal
          className="HCPAutoCancelDialog__modal"
          headerRender={<h2 className="HCPAutoCancelDialog__title">Welcome to aptihealth!</h2>}
          isBottomCloseActive={false}
          isOpenOnRender={true}
          onModalClose={acknowledgeHCPAutoCancellation}
        >
          <HCPAutoCancelDialog
            scheduleHandler={() => {
              acknowledgeHCPAutoCancellation();
              props.history.push("/app/schedule/assign?mode=new");
            }}
          />
        </Portal>
      )}

      {scheduleList && <InstantCall calls={scheduleList} className="NDCPG__container" />}

      {showPaywall && <PatientPaymentBanner />}

      <h1 className="mt-0 mb-3 pt-4 pb-1 order-lg-2 order-1 fs-32 txt-gry">
        <b data-private="true">{firstName ? `Welcome, ${firstName}!` : ""}</b>
      </h1>

      {profile && !isInactive(profile) && (
        <section className="row">
          <NextStepWidget {...props} />
        </section>
      )}

      {profile &&
        (profile.schedule_video_call_date_time || profile.ninety_day_care !== "not_assigned") && (
          <div className="row">
            <div className="col-lg-4 col-12">
              <NextSessionWidget scheduleList={scheduleList} />
            </div>
            <div className="col-lg-4 col-12">
              <TasksWidget {...props} />
            </div>
            <div className="col-lg-4 d-md-block d-none">
              <MessagesWidget {...props} sendBirdContacts={sendBirdContacts} />
            </div>
          </div>
        )}

      <section className="row mb-2">
        <div className="col-lg-6 col-sm-12 col-md-6">
          <ScheduleWidget {...props} />
        </div>

        <div className="col-lg-6 col-sm-12 col-md-6">
          <div className="fs-22 txt-gry pb-2 tw:font-bold">Accomplishments</div>
          <div className="pl-1">
            <AccomplishmentsWidget {...accomplishments} />
          </div>
        </div>
      </section>

      {profile && shouldShowFamilyMembersWidget(profile) && (
        <section className="mt-3">
          <FamilyMembersWidget {...props} />
        </section>
      )}

      <section className="row pb-md-5">
        <div className="col-12 pb-md-5 pb-0">
          <CareTeamWidget {...props} sendBirdContacts={sendBirdContacts} />
        </div>
      </section>
    </div>
  );
};

const mapStateToProps = (state) => {
  return {
    allowInactivePopUp: state.patient.allowInactivePopUp,
    allowWaitListPopUp: state.patient.allowWaitListPopUp,
    daysUntilPasswordReset: state.auth.daysUntilPasswordReset,
    profile: state.auth.profile,
    scheduleList: state.patient.scheduleList,
    showEmailVerified: state.auth.showEmailVerified,
  };
};

function mapDispatchToProps(dispatch) {
  return {
    dispatch,
    ...bindActionCreators(
      {
        disableLoginPopUps,
        fetchActivityList,
        showAlertWithActionAndCustomButton,
        updateReduxProfile,
        updateScheduleList,
      },
      dispatch,
    ),
  };
}

export default connect(mapStateToProps, mapDispatchToProps)(withRouter(MemberHome));

const isInactive = (profile) => {
  return profile && profile.status === "INACTIVE";
};

const isWaitListed = (profile) => {
  return profile?.is_wait_listed === true;
};

const hasAcknowledgedHCPDI = (profile) => {
  return _.get(profile, "acknowledgements.hcp_di_reminder", false);
};

const shouldShowHCPDIModal = (profile) => {
  return (
    profile &&
    profile.group_type === "hcp_program" &&
    profile.schedule_video_call &&
    !hasAcknowledgedHCPDI(profile) &&
    !isInactive(profile)
  );
};

const shouldShowInactiveModal = (profile) => {
  return isInactive(profile);
};

const shouldShowWaitListModal = (profile) => {
  return isWaitListed(profile);
};

const shouldShowFamilyMembersWidget = (profile) => {
  return !!(
    _.get(profile, "enriched_associated_wards", []).length ||
    _.get(profile, "enriched_associated_guardians", []).length
  );
};
